import React, { useRef } from 'react';
import styled, { css, Keyframes } from 'styled-components';

import { BlackSparkleIcon } from 'src/assets/black-sparkle-icon';
import { tracker } from 'src/utils/analytics';
import { useConciergeNavigate } from 'src/dispensary/core-menu/components/header/concierge-nav-button/use-concierge-navigate';
import {
  colorPulse,
  glowLayers,
  GREEN_TRANSPARENT,
  HIGHLIGHT_GOLD,
  LENS_GOLD,
  LENS_GREEN,
  lensLayers,
  ringMaskCss,
  spin,
  SVG_PAD,
  useNeedsDarkening,
} from 'src/dispensary/core-menu/components/header/concierge-nav-button/concierge-button-effects';

const FAB_SIZE = 56;
const CONCIERGE_FAB_LABEL = 'Talk to your Virtual Budtender';
const CONCIERGE_FAB_TOUCH_LABEL = 'AI Concierge';
// Same RGB as HIGHLIGHT_GOLD, alpha 0 — matching RGB avoids the muddy fringe that
// gradients can produce interpolating between differently-colored transparent stops.
const GOLD_TRANSPARENT = 'rgba(242, 193, 77, 0)';

// The idle/ambient shimmer only ever traces the collapsed circle (it's fully faded
// out before the pill finishes expanding), so it's pinned to this constant instead
// of the live ResizeObserver-measured size — otherwise it visibly resizes mid-fade.
const REST_RECT_PROPS = {
  x: SVG_PAD,
  y: SVG_PAD,
  width: FAB_SIZE,
  height: FAB_SIZE,
  rx: FAB_SIZE / 2,
  pathLength: 100,
};

type ConciergeFabProps = {
  cName: string | null;
};

export const ConciergeFab = ({ cName }: ConciergeFabProps): JSX.Element | null => {
  const navigateToConcierge = useConciergeNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const needsDarkening = useNeedsDarkening(buttonRef);

  if (!cName) return null;

  const handleClick = (): void => {
    tracker.conciergeEntryPointClicked({ location: 'floating_button' });
    navigateToConcierge();
  };

  return (
    <Fab ref={buttonRef} type='button' onClick={handleClick} data-testid='concierge-fab'>
      <HoverGlow aria-hidden />
      <Face $darken={needsDarkening}>
        <IconWrap aria-hidden>
          <BlackSparkleIcon width={20} height={20} fill='currentColor' />
        </IconWrap>
        <HoverLabel>{CONCIERGE_FAB_LABEL}</HoverLabel>
        <TouchLabel>{CONCIERGE_FAB_TOUCH_LABEL}</TouchLabel>
      </Face>
      {/* Always rendered — geometry is the constant collapsed-circle size, so it
          doesn't need to wait on the live ResizeObserver measurement below. */}
      <RestStrokeSvg aria-hidden viewBox={`0 0 ${FAB_SIZE + SVG_PAD * 2} ${FAB_SIZE + SVG_PAD * 2}`}>
        <BaseRing {...REST_RECT_PROPS} />
        <GlowGroup>
          {/* static module-level arrays — index keys are safe */}
          {glowLayers.map((layer, i) => (
            <TaperedStroke
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              {...REST_RECT_PROPS}
              $alpha={layer.alpha}
              $color={HIGHLIGHT_GOLD}
              $length={layer.length}
              $travel={layer.travel}
              $width={layer.width}
            />
          ))}
        </GlowGroup>
        {/* static module-level arrays — index keys are safe */}
        {lensLayers.map((layer, i) => (
          <TaperedStroke
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            {...REST_RECT_PROPS}
            $alpha={layer.alpha}
            $color={LENS_GOLD}
            $length={layer.length}
            $pulse
            $travel={layer.travel}
            $width={layer.width}
          />
        ))}
      </RestStrokeSvg>
      {/* Touch devices stay permanently in the expanded pill shape with no discrete
          hover state, so instead of RestStrokeSvg's fixed circle trace, this masked
          gradient ring auto-fits whatever shape/width Face currently renders — no
          measurement needed, so it's correct for any dispensary's branding font. */}
      <IdleShimmerBand aria-hidden>
        <IdleShimmerArc />
      </IdleShimmerBand>
      {/* A plain CSS border instead of an SVG trace — it's a static, non-moving
          outline once hovering starts, so it can just match Face's current box
          directly with no ResizeObserver/JS measurement involved. */}
      <HoverRingBorder aria-hidden />
      <HoverShadeBand aria-hidden>
        <HoverShadeArc />
      </HoverShadeBand>
    </Fab>
  );
};

const Fab = styled.button`
  background: none;
  border: none;
  border-radius: ${FAB_SIZE / 2}px;
  bottom: max(20px, env(safe-area-inset-bottom));
  cursor: pointer;
  left: 20px;
  outline: none;
  padding: 0;
  position: fixed;
  // Below every overlay layer in the app: shared react-modal Modal (100
  // normally, 500 when marked important) and MUI's Drawer/Modal (1200 / 1300
  // in this app's @material-ui/core v4) — so the cart drawer and any modal
  // slide in over the FAB on mobile instead of floating on top of it.
  z-index: 50;

  &:focus-visible {
    outline: 2px solid #0075e0;
    outline-offset: 3px;
  }
`;

const HoverGlow = styled.span`
  background: radial-gradient(closest-side, var(--dcm-color-primary), transparent);
  border-radius: ${FAB_SIZE / 2 + 4}px;
  filter: blur(6px);
  inset: -4px;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  transition: opacity 0.3s ease;

  ${Fab}:hover &,
  ${Fab}:focus-visible & {
    opacity: 0.6;
  }
`;

const Face = styled.span<{ $darken: boolean }>`
  align-items: center;
  background: ${({ $darken }) =>
    $darken
      ? 'linear-gradient(rgba(0, 0, 0, 0.30), rgba(0, 0, 0, 0.30)), var(--dcm-color-primary)'
      : 'var(--dcm-color-primary)'};
  border-radius: ${FAB_SIZE / 2}px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  color: var(--dcm-cart-btn-text-active);
  display: flex;
  gap: 4px;
  height: ${FAB_SIZE}px;
  overflow: hidden;
  position: relative;
  transition: padding 0.25s ease, width 0.25s ease;
  white-space: nowrap;
  width: ${FAB_SIZE}px;

  ${Fab}:focus-visible & {
    padding: 0 18px 0 0;
    width: auto;
  }

  @media (hover: hover) and (pointer: fine) {
    ${Fab}:hover & {
      padding: 0 18px 0 0;
      width: auto;
    }
  }

  // devices with no hover capability (touch): stay expanded at all times
  @media (hover: none) {
    padding: 0 18px 0 0;
    width: auto;
  }
`;

const IconWrap = styled.span`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  width: ${FAB_SIZE}px;
`;

const HoverLabel = styled.span`
  font-size: var(--dcm-font-size-md);
  font-weight: var(--dcm-cart-btn-font-weight);
  line-height: 1.4;
  margin-left: -10px;
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  transition: max-width 0.25s ease, opacity 0.2s ease;

  ${Fab}:focus-visible & {
    max-width: 220px;
    opacity: 1;
  }

  @media (hover: hover) and (pointer: fine) {
    ${Fab}:hover & {
      max-width: 220px;
      opacity: 1;
    }
  }

  // hidden on touch devices — TouchLabel is shown instead
  @media (hover: none) {
    display: none;
  }
`;

const TouchLabel = styled.span`
  display: none;
  font-size: var(--dcm-font-size-md);
  font-weight: var(--dcm-cart-btn-font-weight);
  line-height: 1.4;
  margin-left: -10px;
  white-space: nowrap;

  @media (hover: none) {
    display: inline-block;
  }
`;

// Pinned to a fixed box over the collapsed circle (not `inset`, which stretches to
// Fab's *current* width) — its viewBox never changes, so it must not be stretched
// to match the pill's expanded width, or its content re-centers into empty space.
const RestStrokeSvg = styled.svg`
  height: ${FAB_SIZE + SVG_PAD * 2}px;
  left: -${SVG_PAD}px;
  overflow: visible;
  pointer-events: none;
  position: absolute;
  top: -${SVG_PAD}px;
  width: ${FAB_SIZE + SVG_PAD * 2}px;

  // touch devices never truly leave the expanded pill shape, so this trace of
  // the (never-shown) collapsed circle has nothing to hide behind — hide it
  // outright instead of relying on the :hover/:focus-visible rules below, which
  // touch interactions rarely trigger persistently.
  @media (hover: none) {
    display: none;
  }
`;

const BaseRing = styled.rect`
  fill: none;
  stroke: rgba(255, 255, 255, 0.25);
  stroke-width: 1;
  transition: opacity 0.1s ease;

  // this traces the fixed collapsed-circle size, so once the pill expands past it
  // on hover it needs to hide explicitly instead of being covered by HoverRingBorder
  ${Fab}:hover &,
  ${Fab}:focus-visible & {
    opacity: 0;
  }
`;

const TaperedStroke = styled.rect<{
  $alpha: number;
  $color: string;
  $length: number;
  $pulse?: boolean;
  $travel: Keyframes;
  $width: number;
}>`
  fill: none;
  stroke: ${({ $color }) => $color};
  stroke-dasharray: ${({ $length }) => `${$length} ${100 - $length}`};
  stroke-opacity: ${({ $alpha }) => $alpha};
  stroke-width: ${({ $width }) => $width};
  transition: opacity 0.3s ease;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${({ $pulse, $travel }) =>
      $pulse
        ? css`
            ${$travel} 5s linear infinite,
            ${colorPulse} 6s ease-in-out infinite
          `
        : css`
            ${$travel} 5s linear infinite
          `};
  }

  ${Fab}:hover &,
  ${Fab}:focus-visible & {
    animation-play-state: paused;
    opacity: 0;
  }
`;

const GlowGroup = styled.g`
  filter: blur(4px);
  opacity: 0.4;
  transition: opacity 0.3s ease;

  ${Fab}:hover &,
  ${Fab}:focus-visible & {
    opacity: 0;
  }
`;

const HoverRingBorder = styled.span`
  border-radius: ${FAB_SIZE / 2}px;
  box-shadow: 0 0 0 1px ${HIGHLIGHT_GOLD};
  inset: 0;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  transition: opacity 0.25s ease;

  ${Fab}:hover &,
  ${Fab}:focus-visible & {
    opacity: 1;
  }
`;

const HoverShadeBand = styled.span`
  border-radius: ${FAB_SIZE / 2 + 1}px;
  inset: -1px;
  ${ringMaskCss}
  opacity: 0;
  overflow: hidden;
  padding: 2px;
  pointer-events: none;
  position: absolute;
  transition: opacity 0.3s ease;

  ${Fab}:hover &,
  ${Fab}:focus-visible & {
    opacity: 1;
    transition: opacity 0.4s ease 0.5s;
  }
`;

const HoverShadeArc = styled.span`
  background: conic-gradient(
    ${GREEN_TRANSPARENT} 250deg,
    ${LENS_GREEN} 296deg,
    ${LENS_GREEN} 312deg,
    ${GREEN_TRANSPARENT} 358deg
  );
  height: 260px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 260px;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${spin} 7s linear infinite;
  }
`;

// Same masked-ring technique as HoverShadeBand — it clips to Face's actual
// rendered shape via border-radius/inset alone, so it's correct for any pill
// width without needing to measure it.
const IdleShimmerBand = styled.span`
  border-radius: ${FAB_SIZE / 2 + 1}px;
  display: none;
  inset: -1px;
  ${ringMaskCss}
  overflow: hidden;
  padding: 2px;
  pointer-events: none;
  position: absolute;

  @media (hover: none) {
    display: block;
  }
`;

const IdleShimmerArc = styled.span`
  background: conic-gradient(
    ${GOLD_TRANSPARENT} 0deg,
    ${HIGHLIGHT_GOLD} 35deg,
    ${GOLD_TRANSPARENT} 70deg,
    ${GOLD_TRANSPARENT} 360deg
  );
  // fixed, generously oversized so it fully covers the visible band regardless
  // of how wide the pill ends up being for a given dispensary's font/branding
  height: 400px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 400px;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${spin} 5s linear infinite;
  }
`;
